/* sugar:assets/collection-filters.js@2.1.8 */
/*
 * Collection filters — the filter and sort layer of the Featured Collection section.
 *
 * The section renders one set of filter inputs (snippets/collection-filters.liquid). Every
 * change gathers the checked inputs into a query string, refetches the section through the
 * Section Rendering API and swaps the results, the active chips, the counts and each
 * group's body in place, keeping the group a shopper has open open. The URL is pushed so
 * the page can be shared and the back button walks the filter history.
 *
 * Everything is delegated from the document, so markup that arrives later (the section
 * rendering swap, the theme editor) needs no re-init. The one viewport-dependent piece is
 * the dropdown mode, where only one group may be open at a time.
 */
(function () {
  'use strict';

  if (window.SugarCollectionFilters) return;

  // The mobile layout runs below 750px, or below 1024px when the section moves its desktop layout there.
  var QUERIES = { 750: window.matchMedia('(max-width: 749px)'), 1024: window.matchMedia('(max-width: 1023px)') };
  var cache = {};
  var timer = null;

  function rootOf(el) {
    return el && el.closest ? el.closest('[data-cf]') : null;
  }

  function isMobile(wrap) {
    return (QUERIES[wrap.getAttribute('data-cf-from')] || QUERIES[750]).matches;
  }

  function dropdownMode(wrap) {
    return isMobile(wrap)
      ? wrap.classList.contains('sugar-collection-filters--m-dropdowns')
      : wrap.classList.contains('sugar-collection-filters--d-dropdowns');
  }

  /* The slide-in sidebar on desktop pushes the grid aside; everything else that opens is a sheet. */
  function drawerMode(wrap) {
    return !isMobile(wrap) && wrap.classList.contains('sugar-collection-filters--d-drawer');
  }

  /* Sort can render twice (the menu above the grid and the group in the sheet), each copy its own
     radio group; syncSort keeps every copy on the same value. */
  function currentSort(wrap) {
    var select = wrap.querySelector('select[data-cf-sort]');
    if (select) return select.value;
    var radio = wrap.querySelector('input[data-cf-sort]:checked');
    return radio ? radio.value : '';
  }

  function syncSort(wrap, value) {
    Array.prototype.forEach.call(wrap.querySelectorAll('[data-cf-sort]'), function (input) {
      if (input.tagName === 'SELECT') {
        if (input.value !== value) input.value = value;
        return;
      }
      var on = input.value === value;
      if (input.checked !== on) input.checked = on;
      var label = input.closest('.sugar-collection-filters__value');
      if (label && label.classList.contains('is-active') !== on) label.classList.toggle('is-active', on);
    });
  }

  function params(wrap) {
    var p = new URLSearchParams();
    var inputs = wrap.querySelectorAll('[data-cf-groups] input[name]');
    Array.prototype.forEach.call(inputs, function (input) {
      if (input.disabled || input.hasAttribute('data-cf-sort')) return;
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      if (input.type === 'number' && input.value === '') return;
      p.append(input.name, input.value);
    });
    if (wrap.querySelector('[data-cf-sort]')) {
      var sort = currentSort(wrap);
      if (sort) p.set('sort_by', sort);
    }
    return p.toString();
  }

  function pageUrl(wrap, search) {
    return wrap.getAttribute('data-cf-url') + (search ? '?' + search : '');
  }

  function replaceInner(wrap, fresh, selector) {
    var a = wrap.querySelector(selector);
    var b = fresh.querySelector(selector);
    if (a && b) a.innerHTML = b.innerHTML;
  }

  function swap(wrap, fresh) {
    replaceInner(wrap, fresh, '[data-cf-grid]');
    replaceInner(wrap, fresh, '[data-cf-active]');
    replaceInner(wrap, fresh, '[data-cf-pills]');
    replaceInner(wrap, fresh, '[data-cf-quick]');
    replaceInner(wrap, fresh, '[data-cf-sort-current]');
    var freshSort = fresh.querySelector('[data-cf-sort]') ? currentSort(fresh) : '';
    if (freshSort) syncSort(wrap, freshSort);

    var count = fresh.querySelector('[data-cf-count]');
    if (count) {
      Array.prototype.forEach.call(wrap.querySelectorAll('[data-cf-count]'), function (el) {
        el.innerHTML = count.innerHTML;
      });
    }

    var freshActive = fresh.querySelector('[data-cf-active-count]');
    if (freshActive) {
      Array.prototype.forEach.call(wrap.querySelectorAll('[data-cf-active-count]'), function (active) {
        active.textContent = freshActive.textContent;
        active.hidden = freshActive.hidden;
      });
    }

    var container = wrap.querySelector('[data-cf-groups]');
    if (!container) return;

    var existing = {};
    Array.prototype.forEach.call(container.querySelectorAll('[data-cf-group]'), function (g) {
      existing[g.getAttribute('data-cf-group')] = g;
    });

    var seen = {};
    var focused = document.activeElement;
    Array.prototype.forEach.call(fresh.querySelectorAll('[data-cf-group]'), function (fg) {
      var key = fg.getAttribute('data-cf-group');
      seen[key] = true;
      var g = existing[key];
      if (!g) {
        container.appendChild(fg);
        return;
      }
      var body = g.querySelector('[data-cf-group-body]');
      var freshBody = fg.querySelector('[data-cf-group-body]');
      // A body the shopper is typing in (the price fields) keeps its inputs.
      if (body && freshBody && !(focused && body.contains(focused))) {
        body.innerHTML = freshBody.innerHTML;
      }
      var value = g.querySelector('[data-cf-group-value]');
      var freshValue = fg.querySelector('[data-cf-group-value]');
      if (value && freshValue) value.innerHTML = freshValue.innerHTML;
    });

    Object.keys(existing).forEach(function (key) {
      if (!seen[key]) existing[key].remove();
    });
  }

  function render(wrap, search, push) {
    var sectionId = wrap.getAttribute('data-cf-section');
    var url = wrap.getAttribute('data-cf-url') + '?section_id=' + encodeURIComponent(sectionId) + (search ? '&' + search : '');
    var seq = (wrap._cfSeq = (wrap._cfSeq || 0) + 1);

    wrap.classList.add('is-loading');

    var get = cache[url]
      ? Promise.resolve(cache[url])
      : fetch(url).then(function (r) { return r.text(); }).then(function (text) {
          cache[url] = text;
          return text;
        });

    get
      .then(function (html) {
        if (seq !== wrap._cfSeq) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cf]');
        if (!fresh) {
          window.location.href = pageUrl(wrap, search);
          return;
        }
        swap(wrap, fresh);
        wrap.classList.remove('is-loading');
        if (push) history.pushState({ sugarCf: search }, '', pageUrl(wrap, search));
        if (window.SugarCardGrid) window.SugarCardGrid.init(wrap);
        document.dispatchEvent(new CustomEvent('sugar:collection:rendered', { detail: { root: wrap }, bubbles: true }));
      })
      .catch(function () {
        wrap.classList.remove('is-loading');
      });
  }

  function setOpen(wrap, on, target) {
    if (wrap.classList.contains('is-open') === on && !target) return;
    wrap.classList.toggle('is-open', on);
    Array.prototype.forEach.call(wrap.querySelectorAll('[data-cf-open]'), function (t) {
      t.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    var trigger = wrap.querySelector('[data-cf-open]:not([data-cf-float])');
    // Only the sheet holds the page still; the slide-in sidebar sits beside the grid.
    var lock = on && !drawerMode(wrap);
    if (document.documentElement.style.overflow !== (lock ? 'hidden' : '')) {
      document.documentElement.style.overflow = lock ? 'hidden' : '';
    }
    if (on) {
      var panel = wrap.querySelector('[data-cf-panel]');
      var group = target && wrap.querySelector('[data-cf-group="' + target.replace(/["\\]/g, '\\$&') + '"]');
      if (group) {
        if (group.tagName === 'DETAILS') group.open = true;
        var groups = wrap.querySelector('[data-cf-groups]');
        if (groups) groups.scrollTop = group.offsetTop - groups.offsetTop;
      }
      var first = (group && group.querySelector('summary, input:not([disabled])')) || (panel && panel.querySelector('summary, input:not([disabled]), button'));
      if (first) first.focus({ preventScroll: true });
    } else if (trigger) {
      trigger.focus({ preventScroll: true });
    }
  }

  function closePopovers(wrap, except) {
    Array.prototype.forEach.call(wrap.querySelectorAll('[data-cf-group][open]'), function (g) {
      if (g !== except) g.removeAttribute('open');
    });
  }

  /* A sticky quick filter row: its height goes on the root as --cf-quick-h, so a sticky sidebar
     beside the grid sticks under the row instead of behind it. Written only when it changes. */
  var quickObserver = 'ResizeObserver' in window
    ? new ResizeObserver(function (entries) {
        var heights = entries.map(function (entry) { return Math.round(entry.target.offsetHeight); });
        entries.forEach(function (entry, i) {
          var wrap = rootOf(entry.target);
          if (!wrap || wrap._cfQuickH === heights[i]) return;
          wrap._cfQuickH = heights[i];
          wrap.style.setProperty('--cf-quick-h', heights[i] + 'px');
        });
      })
    : null;

  function armQuick(scope) {
    if (!quickObserver) return;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-cf].sugar-collection-filters--quick-sticky'), function (wrap) {
      var row = wrap.querySelector('[data-cf-quick]');
      if (!row || wrap._cfQuickRow === row) return;
      wrap._cfQuickRow = row;
      quickObserver.observe(row);
    });
  }

  function applyMode(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    armFloat(host);
    armQuick(host);
    Array.prototype.forEach.call(host.querySelectorAll('[data-cf]'), function (wrap) {
      if (wrap.classList.contains('is-open')) setOpen(wrap, false);
      if (dropdownMode(wrap)) {
        closePopovers(wrap);
      } else if (wrap.classList.contains('sugar-collection-filters--open-groups')) {
        Array.prototype.forEach.call(wrap.querySelectorAll('[data-cf-group]'), function (g) {
          g.setAttribute('open', '');
        });
      }
    });
  }

  /* Price slider: the thumbs write the number fields, the fields move the thumbs. */
  function priceBody(el) {
    return el.closest('[data-cf-group-body]');
  }

  function paintRange(body) {
    var range = body.querySelector('[data-cf-price-range]');
    if (!range) return;
    var lo = range.querySelector('[data-cf-range="min"]');
    var hi = range.querySelector('[data-cf-range="max"]');
    var max = parseFloat(lo.max) || 0;
    var a = parseFloat(lo.value) || 0;
    var b = hi.value === '' ? max : parseFloat(hi.value);
    if (max > 0) {
      range.style.setProperty('--cf-lo', (a / max) * 100 + '%');
      range.style.setProperty('--cf-hi', (b / max) * 100 + '%');
    }
  }

  function syncFromRange(range, moved) {
    var body = priceBody(range);
    var lo = body.querySelector('[data-cf-range="min"]');
    var hi = body.querySelector('[data-cf-range="max"]');
    var max = parseFloat(lo.max) || 0;
    var a = parseFloat(lo.value) || 0;
    var b = parseFloat(hi.value);
    if (isNaN(b)) b = max;
    if (a > b) {
      if (moved === lo) { b = a; hi.value = b; } else { a = b; lo.value = a; }
    }
    var minField = body.querySelector('[data-cf-price="min"]');
    var maxField = body.querySelector('[data-cf-price="max"]');
    if (minField) minField.value = a > 0 ? a : '';
    if (maxField) maxField.value = b < max ? b : '';
    paintRange(body);
  }

  function syncFromFields(body) {
    var lo = body.querySelector('[data-cf-range="min"]');
    var hi = body.querySelector('[data-cf-range="max"]');
    if (!lo || !hi) return;
    var minField = body.querySelector('[data-cf-price="min"]');
    var maxField = body.querySelector('[data-cf-price="max"]');
    lo.value = minField && minField.value !== '' ? minField.value : 0;
    hi.value = maxField && maxField.value !== '' ? maxField.value : hi.max;
    paintRange(body);
  }

  document.addEventListener('input', function (event) {
    var range = event.target;
    if (!range.matches || !range.matches('[data-cf-range]')) return;
    syncFromRange(range.closest('[data-cf-price-range]'), range);
  });

  /* Floating filter button on mobile: shows once the bar has scrolled off the top, while
     the grid is still on screen. */
  var floatObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          var wrap = rootOf(el);
          if (!wrap) return;
          if (el.matches('.sugar-collection-filters__bar')) {
            wrap._cfBarAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          } else {
            wrap._cfInView = entry.isIntersecting;
          }
          wrap.classList.toggle('is-floating', !!(wrap._cfBarAbove && wrap._cfInView));
        });
      }, { threshold: 0 })
    : null;

  function armFloat(scope) {
    if (!floatObserver) return;
    var host = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(host.querySelectorAll('[data-cf].sugar-collection-filters--sticky-button'), function (wrap) {
      if (wrap._cfFloatArmed) return;
      wrap._cfFloatArmed = true;
      var bar = wrap.querySelector('.sugar-collection-filters__bar');
      if (bar) floatObserver.observe(bar);
      floatObserver.observe(wrap);
    });
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    var wrap = rootOf(input);
    if (!wrap || !input.closest('[data-cf-groups], [data-cf-sort-menu], [data-cf-group-body]')) return;
    if (input.hasAttribute('data-cf-sort')) {
      syncSort(wrap, input.value);
      var menu = input.closest('details[data-cf-sort-menu]');
      if (menu) menu.removeAttribute('open');
    }
    if (input.type === 'number' && input.matches('[data-cf-price]')) {
      syncFromFields(priceBody(input));
    }
    if (input.type === 'number') {
      var min = parseFloat(input.min);
      var max = parseFloat(input.max);
      var v = parseFloat(input.value);
      if (!isNaN(v)) {
        if (!isNaN(min) && v < min) input.value = min;
        if (!isNaN(max) && v > max) input.value = max;
      }
    }
    clearTimeout(timer);
    timer = setTimeout(function () {
      render(wrap, params(wrap), true);
    }, input.type === 'number' ? 400 : 120);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target.matches && event.target.matches('[data-cf-price]')) {
      event.preventDefault();
      event.target.blur();
      return;
    }
    if (event.key !== 'Escape') return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-cf]'), function (wrap) {
      if (wrap.classList.contains('is-open')) setOpen(wrap, false);
      if (dropdownMode(wrap)) closePopovers(wrap);
    });
  });

  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-cf-remove]');
    if (remove) {
      var wrap = rootOf(remove);
      if (!wrap) return;
      event.preventDefault();
      var url = new URL(remove.getAttribute('href'), window.location.href);
      render(wrap, url.searchParams.toString(), true);
      return;
    }

    var opener = event.target.closest('[data-cf-open]');
    if (opener) {
      var w1 = rootOf(opener);
      if (!w1) return;
      // The slide-in sidebar's button toggles it; a group pill opens the sheet at its group.
      if (drawerMode(w1) && w1.classList.contains('is-open')) setOpen(w1, false);
      else setOpen(w1, true, opener.getAttribute('data-cf-target'));
      return;
    }

    var quickValue = event.target.closest('[data-cf-quick-value]');
    if (quickValue) {
      var w3 = rootOf(quickValue);
      var row = quickValue.closest('[data-cf-quick]');
      if (!w3 || !row) return;
      var name = row.getAttribute('data-cf-quick');
      var value = quickValue.getAttribute('data-cf-quick-value');
      Array.prototype.forEach.call(row.querySelectorAll('[data-cf-quick-value]'), function (pill) {
        var on = pill === quickValue;
        pill.classList.toggle('is-active', on);
        pill.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // One value at a time: the pill's own value, or none for All.
      var matched = false;
      Array.prototype.forEach.call(w3.querySelectorAll('[data-cf-groups] input[type="checkbox"]'), function (input) {
        if (input.name !== name) return;
        matched = true;
        input.checked = value !== '' && input.value === value;
      });
      var search = params(w3);
      if (!matched && value !== '') {
        var p = new URLSearchParams(search);
        p.set(name, value);
        search = p.toString();
      }
      render(w3, search, true);
      return;
    }

    var closer = event.target.closest('[data-cf-close]');
    if (closer) {
      var w2 = rootOf(closer);
      if (w2) setOpen(w2, false);
      return;
    }

    Array.prototype.forEach.call(document.querySelectorAll('[data-cf]'), function (wrap) {
      Array.prototype.forEach.call(wrap.querySelectorAll('details[data-cf-sort-menu][open]'), function (menu) {
        if (!menu.contains(event.target)) menu.removeAttribute('open');
      });
      if (!dropdownMode(wrap)) return;
      Array.prototype.forEach.call(wrap.querySelectorAll('[data-cf-group][open]'), function (g) {
        if (!g.contains(event.target)) g.removeAttribute('open');
      });
    });
  });

  // toggle does not bubble; listen in the capture phase.
  document.addEventListener(
    'toggle',
    function (event) {
      var g = event.target;
      if (!g.matches || !g.matches('[data-cf-group]') || !g.open) return;
      var wrap = rootOf(g);
      if (!wrap || !dropdownMode(wrap)) return;
      closePopovers(wrap, g);
      if (isMobile(wrap)) {
        // The body is absolutely positioned against the section, under the whole row.
        var row = g.closest('[data-cf-groups]') || g;
        var top = row.getBoundingClientRect().bottom - wrap.getBoundingClientRect().top + 2;
        wrap.style.setProperty('--cf-drop-top', Math.round(top) + 'px');
      }
    },
    true
  );

  window.addEventListener('popstate', function () {
    var search = window.location.search.replace(/^\?/, '');
    Array.prototype.forEach.call(document.querySelectorAll('[data-cf]'), function (wrap) {
      render(wrap, search, false);
    });
  });

  var onMode = function () {
    applyMode(document);
  };
  Object.keys(QUERIES).forEach(function (key) {
    var query = QUERIES[key];
    if (query.addEventListener) {
      query.addEventListener('change', onMode);
    } else if (query.addListener) {
      query.addListener(onMode);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      applyMode(document);
    });
  } else {
    applyMode(document);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      applyMode(event.target);
    });
  }

  window.SugarCollectionFilters = { render: render, applyMode: applyMode };
})();
